import React, { useState, useEffect } from 'react';
import { Shield, Download, Filter, Trash2, Image, Video, RefreshCw, UserX } from 'lucide-react';

export default function DsgvoDeleteLog() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [stats, setStats] = useState({ videos: 0, images: 0, accounts: 0, total: 0 });

  useEffect(() => {
    loadEntries();
  }, [filterType]);

  const loadEntries = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('snapshot_token');
      
      const params = new URLSearchParams();
      if (filterType !== 'all') {
        params.append('action_type', filterType);
      }
      params.append('limit', '500');
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/system-audit/dsgvo-deletions?${params}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (!response.ok) throw new Error('Fehler beim Laden');
      
      const data = await response.json();
      setEntries(data.entries || []);
      
      // Stats berechnen
      const videos = data.entries.filter(e => e.action === 'VIDEO_DELETED').length;
      const images = data.entries.filter(e => e.action === 'IMAGE_DELETED').length;
      const accounts = data.entries.filter(e => e.action === 'ACCOUNT_DELETED').length;
      setStats({ videos, images, accounts, total: data.entries.length });
      
      setLoading(false);
    } catch (err) {
      console.error('Error:', err);
      setError('Fehler beim Laden');
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    try {
      const token = localStorage.getItem('snapshot_token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/system-audit/dsgvo-deletions/export?format=${format}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (!response.ok) throw new Error('Export fehlgeschlagen');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dsgvo_loeschprotokoll.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error:', err);
      alert('Export fehlgeschlagen');
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <Shield className="w-8 h-8 text-[#10B981]" />
          <div>
            <h1 className="text-2xl font-bold text-white">DSGVO Löschprotokoll</h1>
            <p className="text-white/60 text-sm">Lückenlose Dokumentation aller Löschungen</p>
          </div>
        </div>
        
        <div className="flex gap-2">
          <button
            onClick={() => loadEntries()}
            className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg flex items-center gap-2 transition-all"
          >
            <RefreshCw className="w-4 h-4" />
            Aktualisieren
          </button>
          <button
            onClick={() => handleExport('csv')}
            className="px-4 py-2 bg-[#10B981] hover:bg-[#0d9668] text-black font-semibold rounded-lg flex items-center gap-2 transition-all"
          >
            <Download className="w-4 h-4" />
            CSV Export
          </button>
          <button
            onClick={() => handleExport('json')}
            className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg flex items-center gap-2 transition-all"
          >
            <Download className="w-4 h-4" />
            JSON
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white/5 border border-white/10 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
              <Trash2 className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.total}</p>
              <p className="text-white/60 text-sm">Gesamt</p>
            </div>
          </div>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <Video className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.videos}</p>
              <p className="text-white/60 text-sm">Videos</p>
            </div>
          </div>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <Image className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.images}</p>
              <p className="text-white/60 text-sm">Bilder</p>
            </div>
          </div>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
              <UserX className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.accounts}</p>
              <p className="text-white/60 text-sm">Accounts</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-4 mb-4">
        <Filter className="w-5 h-5 text-white/60" />
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="bg-white/10 border border-white/20 text-white rounded-lg px-4 py-2"
        >
          <option value="all">Alle Löschungen</option>
          <option value="VIDEO_DELETED">Nur Videos</option>
          <option value="IMAGE_DELETED">Nur Bilder</option>
          <option value="GENERATION_DELETED">Generationen</option>
          <option value="ACCOUNT_DELETED">Account-Löschungen</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-white/60">Laden...</div>
        ) : error ? (
          <div className="p-8 text-center text-red-400">{error}</div>
        ) : entries.length === 0 ? (
          <div className="p-8 text-center text-white/60">
            <Shield className="w-12 h-12 mx-auto mb-3 text-white/20" />
            <p>Noch keine Löschungen dokumentiert</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/5">
                <tr>
                  <th className="text-left p-3 text-white/60 font-medium">Zeitpunkt</th>
                  <th className="text-left p-3 text-white/60 font-medium">Aktion</th>
                  <th className="text-left p-3 text-white/60 font-medium">ID</th>
                  <th className="text-left p-3 text-white/60 font-medium">User</th>
                  <th className="text-left p-3 text-white/60 font-medium">Dateien</th>
                  <th className="text-left p-3 text-white/60 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry, idx) => (
                  <tr key={idx} className="border-t border-white/5 hover:bg-white/5">
                    <td className="p-3 text-white/80 font-mono text-xs">
                      {formatDate(entry.timestamp)}
                    </td>
                    <td className="p-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                        entry.action === 'VIDEO_DELETED'
                          ? 'bg-blue-500/20 text-blue-300'
                          : entry.action === 'IMAGE_DELETED'
                          ? 'bg-purple-500/20 text-purple-300'
                          : entry.action === 'ACCOUNT_DELETED'
                          ? 'bg-yellow-500/20 text-yellow-300'
                          : 'bg-orange-500/20 text-orange-300'
                      }`}>
                        {entry.action === 'VIDEO_DELETED' && <Video className="w-3 h-3" />}
                        {entry.action === 'IMAGE_DELETED' && <Image className="w-3 h-3" />}
                        {entry.action === 'ACCOUNT_DELETED' && <UserX className="w-3 h-3" />}
                        {entry.action}
                      </span>
                    </td>
                    <td className="p-3 text-white/60 font-mono text-xs">
                      {entry.action === 'ACCOUNT_DELETED' ? (entry.user_email || entry.user_id?.slice(0, 8)) : (entry.video_id || entry.generation_id || '-')}
                      {entry.image_index !== undefined && `[${entry.image_index}]`}
                    </td>
                    <td className="p-3 text-white/60 font-mono text-xs">
                      {entry.user_id ? entry.user_id.substring(0, 8) + '...' : '-'}
                    </td>
                    <td className="p-3 text-white/60 text-xs">
                      {entry.files_deleted?.length > 0 ? (
                        <span className="text-green-400">✓ {entry.files_deleted.length} Datei(en)</span>
                      ) : (
                        <span className="text-white/40">Nur DB</span>
                      )}
                    </td>
                    <td className="p-3">
                      {entry.dsgvo_compliant ? (
                        <span className="text-green-400 text-xs font-medium">✓ DSGVO</span>
                      ) : (
                        <span className="text-yellow-400 text-xs">⚠️</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="mt-6 bg-[#10B981]/10 border border-[#10B981]/30 rounded-xl p-4">
        <h3 className="text-[#10B981] font-semibold mb-2">📋 Für DSGVO-Prüfer</h3>
        <ul className="text-white/70 text-sm space-y-1">
          <li>• Jede Löschung wird mit Zeitstempel und User-ID dokumentiert</li>
          <li>• Lokale Dateien (Videos) werden physisch vom Server gelöscht</li>
          <li>• Export als CSV/JSON für Audit-Zwecke verfügbar</li>
          <li>• Favoriten werden automatisch mit entfernt</li>
        </ul>
      </div>
    </div>
  );
}
